import { useState } from "react";
import { C } from "./re-colors.ts";
import { BOOK_OF_BUSINESS, CUSTOMER_RISK_PROFILES } from "./re-data.ts";
import { fmt, KpiCard, PH, Card, KVList, Bdg } from "./re-components.tsx";
import { DocModal } from "./re-modals.tsx";

// ── BOOK OF BUSINESS ──────────────────────────────────────────────
export function BookOfBusiness({
  onLog,
}: {
  onLog: (action: string, detail: string, category: string) => void;
}) {
  const [selected, setSelected] = useState<typeof BOOK_OF_BUSINESS[0] | null>(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("revenue");
  const [search, setSearch] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInsight, setAiInsight] = useState("");
  const [doc, setDoc] = useState<{ title: string; prompt: string } | null>(null);

  const riskColor: Record<string, string>   = { high: C.burgundy, medium: C.gold, low: C.forest };
  const statusColor: Record<string, string> = { Active: C.forest, Watch: C.gold, Prospect: C.navy };

  const filtered = BOOK_OF_BUSINESS
    .filter(c => filter === "all" || c.status.toLowerCase() === filter)
    .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.type.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) =>
      sort === "revenue" ? b.annualRevenue - a.annualRevenue :
      sort === "loan"    ? b.loan - a.loan :
      sort === "score"   ? b.score - a.score : 0
    );

  const totalLoan  = BOOK_OF_BUSINESS.reduce((s, c) => s + c.loan, 0);
  const totalDep   = BOOK_OF_BUSINESS.reduce((s, c) => s + c.dep, 0);
  const totalRev   = BOOK_OF_BUSINESS.reduce((s, c) => s + c.annualRevenue, 0);
  const watchCount = BOOK_OF_BUSINESS.filter(c => c.status === "Watch").length;

  async function getAIInsight(client: typeof BOOK_OF_BUSINESS[0]) {
    setAiLoading(true);
    setAiInsight("");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 400,
          system: "You are a relationship banker AI agent. Give a concise 3-bullet client strategy. Use specific numbers. Be direct about risks and next best actions.",
          messages: [{
            role: "user",
            content: `Client: ${client.name}, ${client.type}, Score: ${client.score}, Loan: $${(client.loan / 1e6).toFixed(1)}M, Deposits: $${(client.dep / 1e6).toFixed(1)}M, Products: ${client.prods.length}, Annual Revenue: $${(client.annualRevenue / 1e3).toFixed(0)}K, Risk: ${client.risk}, Status: ${client.status}, Action: ${client.action}, NBA: ${client.nba}. DSCR: ${client.dscr || "N/A"}, LTV: ${client.ltv ? `${(client.ltv * 100).toFixed(0)}%` : "N/A"}. Provide 3 specific next best actions with expected outcomes.`,
          }],
        }),
      });
      const d = await res.json();
      setAiInsight(d.content?.find((b: any) => b.type === "text")?.text || "Unable to generate.");
      onLog("AI Client Analysis", client.name, "AI Analysis");
    } catch {
      setAiInsight("Connection error.");
    }
    setAiLoading(false);
  }

  // ── CLIENT DETAIL VIEW ─────────────────────────────────────────
  if (selected) {
    const c = selected;
    return (
      <div>
        <PH
          title={c.name}
          sub={`${c.type} · ${c.city} · ${c.industry} · RM: ${c.rm}`}
          back={() => { setSelected(null); setAiInsight(""); }}
          accentColor={riskColor[c.risk]}
        />
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* KPI Row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
              {[
                ["Loan Balance",       c.loan > 0 ? fmt(c.loan) : "None", c.loan > 0 ? C.text : C.muted],
                ["Deposits",           fmt(c.dep),                          C.text ],
                ["Annual Revenue",     `$${(c.annualRevenue / 1e3).toFixed(0)}K`, C.forest],
                ["Relationship Score", String(c.score), c.score > 80 ? C.forest : c.score > 70 ? C.gold : C.burgundy],
              ].map(([l, v, clr], i) => (
                <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>{l}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: clr }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Relationship Profile */}
            <Card>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Relationship Profile</div>
              <KVList rows={[
                ["Customer Since", String(c.since)],
                ["Industry",       c.industry],
                ["City",           c.city],
                ["Employees",      String(c.employees)],
                ["Credit Rating",  c.creditRating || "N/A"],
                c.dscr ? ["DSCR", `${c.dscr}x`, c.dscr >= 1.25 ? C.forest : c.dscr >= 1.10 ? C.gold : C.burgundy] : null,
                c.ltv  ? ["LTV",  `${(c.ltv * 100).toFixed(0)}%`, c.ltv <= 0.65 ? C.forest : c.ltv <= 0.75 ? C.gold : C.burgundy] : null,
                ["Next Maturity",  c.nextMaturity || "N/A"],
                ["Last Contact",   c.lastContact],
                ["Collateral",     c.collateral],
              ].filter(Boolean) as string[][]} />
            </Card>

            {/* Products */}
            <Card>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Products ({c.prods.length})</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {c.prods.map((p, i) => (
                  <span key={i} style={{ padding: "5px 12px", borderRadius: 20, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, color: C.text }}>{p}</span>
                ))}
              </div>
              <div style={{ marginTop: 12, padding: "10px 12px", background: C.forestBg, border: `1px solid ${C.forest}25`, borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: C.forest, fontWeight: 700 }}>
                  💡 Cross-sell opportunity: {c.prods.length < 4 ? "Treasury Management, Business Visa" : c.prods.length < 5 ? "Equipment Financing" : "SBA Refinance"}
                </div>
              </div>
            </Card>

            {/* Action Required */}
            <Card style={{ background: `${riskColor[c.risk]}06`, border: `1px solid ${riskColor[c.risk]}30` }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 8 }}>Current Action Required</div>
              <div style={{ fontSize: 13, color: riskColor[c.risk], fontWeight: 600, marginBottom: 6 }}>{c.action}</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>Next Best Action: {c.nba}</div>
            </Card>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* AI Strategy */}
            <Card style={{ background: `${C.aiPurple}06`, border: `1px solid ${C.aiPurple}30` }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.aiPurple, marginBottom: 10 }}>✦ AI Client Strategy</div>
              {aiInsight ? (
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiInsight}</div>
              ) : (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <button
                    onClick={() => getAIInsight(c)}
                    style={{ background: C.aiPurple, color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", cursor: "pointer", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", gap: 8, margin: "0 auto" }}
                  >
                    {aiLoading
                      ? [0, 1, 2].map(i => <span key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", display: "inline-block", animation: `dot .9s ${i * .2}s infinite` }} />)
                      : <>✦ Generate AI Strategy</>}
                  </button>
                  <div style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>Tailored to this client's profile</div>
                </div>
              )}
            </Card>

            {/* Revenue Summary */}
            <Card>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 10 }}>Revenue Summary</div>
              <KVList rows={[
                ["YTD Revenue",    `$${(c.ytdRevenue / 1e3).toFixed(0)}K`,    C.forest],
                ["Annual Revenue", `$${(c.annualRevenue / 1e3).toFixed(0)}K`, C.text  ],
                ["Est. Cross-Sell",`+$${c.prods.length < 4 ? "12K" : c.prods.length < 5 ? "8K" : "5K"}`, C.gold],
              ]} />
            </Card>

            {/* Generate Memo */}
            <button
              onClick={() => {
                setDoc({
                  title: `Client Profile: ${c.name}`,
                  prompt: `Generate a comprehensive relationship banker memo for ${c.name}. Details: ${c.type}, loan $${(c.loan / 1e6).toFixed(1)}M, deposits $${(c.dep / 1e6).toFixed(1)}M, ${c.prods.length} products, score ${c.score}, status ${c.status}. Current issue: ${c.action}. Next best action: ${c.nba}. Include: relationship summary, credit snapshot, retention strategy, cross-sell recommendations, and action plan.`,
                });
                onLog("Generated client memo", c.name, "Document");
              }}
              style={{ background: C.burgundy, color: "#fff", border: "none", borderRadius: 10, padding: "11px", cursor: "pointer", fontWeight: 700, fontSize: 13 }}
            >
              📄 Generate Client Memo
            </button>
            <button
              onClick={() => setSelected(null)}
              style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 12, color: C.muted }}
            >
              ← Back to Book of Business
            </button>
          </div>
        </div>
        {doc && <DocModal title={doc.title} personaName="Jordan Reed" prompt={doc.prompt} bankCtx="" onClose={() => setDoc(null)} />}
      </div>
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────────
  return (
    <div>
      <PH title="Book of Business" sub="Jordan Reed · Relationship Manager · Demo Regional Bank · As of March 2, 2026" accentColor={C.forest} />

      {/* KPI Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 18 }}>
        <KpiCard label="Total Loans"    value={fmt(totalLoan)} sub="+$2.1M MTD"      trend="up" spark={[43.1,44.2,45.1,45.8,46.4,47.3]} color={C.forest}   />
        <KpiCard label="Total Deposits" value={fmt(totalDep)}  sub="-$340K MTD"      trend="dn"                                          color={C.gold}     />
        <KpiCard label="Annual Revenue" value={`$${(totalRev / 1e3).toFixed(0)}K`} sub="8 relationships"                               color={C.text}     />
        <KpiCard label="Watch Credits"  value={watchCount}     sub="Require attention" trend="dn"                                        color={C.burgundy} />
      </div>

      {/* Filters & Search */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search clients…"
          style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${C.border}`, fontSize: 12, outline: "none", width: 200, color: C.text, background: C.card }}
          onFocus={e => (e.target.style.borderColor = C.forest)}
          onBlur={e => (e.target.style.borderColor = C.border)}
        />
        <div style={{ display: "flex", gap: 6 }}>
          {["all", "active", "watch", "prospect"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ fontSize: 11, padding: "6px 14px", borderRadius: 20, border: `1px solid ${filter === f ? C.forest : C.border}`, background: filter === f ? C.forestBg : "transparent", color: filter === f ? C.forest : C.muted, cursor: "pointer", fontWeight: filter === f ? 700 : 400, textTransform: "capitalize" }}>
              {f}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.muted }}>Sort:</span>
          {[["revenue", "Revenue"], ["loan", "Loan $"], ["score", "Score"]].map(([k, l]) => (
            <button key={k} onClick={() => setSort(k)} style={{ fontSize: 11, padding: "5px 11px", borderRadius: 20, border: `1px solid ${sort === k ? C.forest : C.border}`, background: sort === k ? C.forestBg : "transparent", color: sort === k ? C.forest : C.muted, cursor: "pointer", fontWeight: sort === k ? 700 : 400 }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Client Cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((c, i) => (
          <div
            key={i}
            onClick={() => { setSelected(c); setAiInsight(""); onLog("Viewed client", c.name, "CRM"); }}
            style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", cursor: "pointer", transition: "all .18s" }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 20px rgba(42,31,24,.10)"; (e.currentTarget as HTMLElement).style.borderColor = C.borderLight; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = ""; (e.currentTarget as HTMLElement).style.borderColor = C.border; }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ width: 4, background: riskColor[c.risk], alignSelf: "stretch", flexShrink: 0 }} />
              <div style={{ padding: "13px 16px", flex: 1 }}>
                {/* Main row */}
                <div style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 1fr 1fr 1fr 160px auto", gap: 12, alignItems: "center" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: `${riskColor[c.risk]}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: riskColor[c.risk], flexShrink: 0 }}>
                        {c.name[0]}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{c.type} · {c.city}</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 2 }}>Loan</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{c.loan > 0 ? fmt(c.loan) : "—"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 2 }}>Deposits</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(c.dep)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 2 }}>Revenue</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.forest }}>${(c.annualRevenue / 1e3).toFixed(0)}K</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 2 }}>Score</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: c.score > 80 ? C.forest : c.score > 70 ? C.gold : C.burgundy }}>{c.score}</div>
                  </div>
                  {/* At-Risk Score column */}
                  {(() => {
                    const p = CUSTOMER_RISK_PROFILES[c.id];
                    if (!p) return <div />;
                    const sc = p.atRiskScore > 0.70 ? C.burgundy : p.atRiskScore > 0.40 ? C.gold : C.forest;
                    return (
                      <div>
                        <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 4 }}>At-Risk</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <div style={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${sc}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ fontSize: 10, fontWeight: 900, color: sc }}>{Math.round(p.atRiskScore * 100)}</span>
                          </div>
                          <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2 }}>
                            <div style={{ height: "100%", width: `${p.atRiskScore * 100}%`, background: sc, borderRadius: 2 }} />
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: 11, color: riskColor[c.risk], fontWeight: 600, lineHeight: 1.4 }}>{c.action}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end" }}>
                    <Bdg label={c.status} color={statusColor[c.status]} />
                    <div style={{ fontSize: 9, color: C.dim }}>View →</div>
                  </div>
                </div>
                {/* Signal row — only when at-risk profile exists and score is notable */}
                {(() => {
                  const p = CUSTOMER_RISK_PROFILES[c.id];
                  if (!p || p.atRiskScore < 0.40) return null;
                  const sc = p.atRiskScore > 0.70 ? C.burgundy : C.gold;
                  return (
                    <div style={{ marginTop: 7, paddingTop: 7, borderTop: `1px dashed ${C.border}`, fontSize: 11, color: sc, fontWeight: 600 }}>
                      ⚡ {p.signal}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <Card><div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>No clients match your filters.</div></Card>
      )}

      {/* Summary Strip */}
      <div style={{ marginTop: 16, padding: "12px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, display: "flex", gap: 24 }}>
        {[["Active", "5", C.forest], ["Watch", "3", C.gold], ["Prospects", "1", C.navy], ["Avg Products", "3.8", C.text], ["YTD Retention", "94%", C.forest]].map(([l, v, c], i) => (
          <div key={i}>
            <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .5 }}>{l}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: c }}>{v}</div>
          </div>
        ))}
        <div style={{ marginLeft: "auto", fontSize: 11, color: C.muted, display: "flex", alignItems: "center" }}>Click any client to view full profile + AI strategy</div>
      </div>
    </div>
  );
}
